package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarDockDestination
import com.partejar.app.core.ui.components.PartejarDockScaffold
import com.partejar.app.core.ui.components.PartejarList
import com.partejar.app.core.ui.components.PartejarListPhase
import com.partejar.app.core.ui.components.PartejarMotion
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarThemeToggleAction
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.components.partejarPressFeedback
import com.partejar.app.core.ui.domain.gestante.GestanteStage
import com.partejar.app.core.ui.domain.gestante.GestanteSummary
import com.partejar.app.core.ui.domain.gestante.GestanteSummaryCard
import com.partejar.app.core.ui.domain.gestante.GestanteSummaryStatus
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteListItemData
import com.partejar.app.feature.gestantes.data.GestanteListStage
import org.koin.compose.koinInject

@Composable
fun GestantesScreen(
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    onOpenDetail: (Long) -> Unit,
    viewModel: GestantesViewModel = koinInject()
) {
    val state by viewModel.uiState.collectAsState()
    val query by viewModel.query.collectAsState()
    val stages by viewModel.selectedStages.collectAsState()
    LaunchedEffect(Unit) { viewModel.retry() }

    GestantesContent(
        state = state,
        query = query,
        selectedStages = stages,
        isDarkTheme = isDarkTheme,
        onThemeToggle = onThemeToggle,
        onQueryChanged = viewModel::onQueryChanged,
        onStageToggled = viewModel::toggleStage,
        onLoadNextPage = viewModel::loadNextPage,
        onRetry = viewModel::retry,
        onOpenDetail = onOpenDetail
    )
}

@Composable
fun GestantesContent(
    state: GestantesUiState,
    query: String,
    selectedStages: Set<GestanteListStage>,
    isDarkTheme: Boolean,
    onThemeToggle: () -> Unit,
    onQueryChanged: (String) -> Unit,
    onStageToggled: (GestanteListStage) -> Unit,
    onLoadNextPage: () -> Unit,
    onRetry: () -> Unit,
    onOpenDetail: (Long) -> Unit
) {
    val spacing = PartejarTheme.spacing

    PartejarDockScaffold(
        topBar = {
            PartejarTopBar(
                title = "Gestantes",
                leadingContent = {
                    Box(
                        Modifier
                            .padding(start = spacing.space16)
                            .size(PartejarTheme.dimensions.minimumTouchTarget),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(PartejarIcons.Navigation.gestantes, contentDescription = null)
                    }
                },
                actions = {
                    Surface(
                        modifier = Modifier.padding(end = spacing.space16),
                        shape = PartejarShapes.pill,
                        color = MaterialTheme.colorScheme.surfaceVariant
                    ) {
                        PartejarThemeToggleAction(isDarkTheme, onThemeToggle)
                    }
                }
            )
        }
    ) { scaffoldPadding, dockClearance ->
        Column(
            Modifier
                .fillMaxSize()
                .padding(scaffoldPadding)
        ) {
            PartejarTextField(
                value = query,
                onValueChange = onQueryChanged,
                label = { Text("Buscar gestante") },
                modifier = Modifier.padding(horizontal = spacing.space16),
                leadingIcon = { Icon(PartejarIcons.Actions.search, contentDescription = null) }
            )

            StatusFilters(selectedStages, onStageToggled)

            val content = state as? GestantesUiState.Content
            PartejarList(
                items = content?.items.orEmpty(),
                key = GestanteListItemData::id,
                phase = when (state) {
                    GestantesUiState.Loading -> PartejarListPhase.LOADING
                    is GestantesUiState.Error -> PartejarListPhase.ERROR
                    is GestantesUiState.Empty -> PartejarListPhase.EMPTY
                    is GestantesUiState.Content -> PartejarListPhase.CONTENT
                },
                modifier = Modifier.weight(1f),
                bottomClearance = dockClearance,
                isRefreshing = content?.isRefreshing == true,
                hasNextPage = content?.hasNextPage == true,
                isLoadingNextPage = content?.isLoadingNextPage == true,
                hasNextPageError = content?.nextPageError != null,
                onLoadNextPage = onLoadNextPage,
                loadingContent = { GestantesListSkeleton() },
                refreshingContent = {},
                nextPageLoadingContent = { GestanteCardSkeleton() },
                emptyContent = {
                    EmptyGestantes((state as? GestantesUiState.Empty)?.hasActiveSearch == true)
                },
                errorContent = {
                    InitialError((state as? GestantesUiState.Error)?.message.orEmpty(), onRetry)
                },
                nextPageErrorContent = {
                    PartejarAlert(
                        title = content?.nextPageError.orEmpty(),
                        tone = PartejarAlertTone.ATTENTION
                    ) {
                        TextButton(onClick = onLoadNextPage) { Text("Tentar novamente") }
                    }
                }
            ) { item ->
                GestanteSummaryCard(
                    summary = item.toSummary(),
                    onClick = { onOpenDetail(item.id.toLong()) }
                )
            }
        }
    }
}

@Composable
private fun GestantesListSkeleton() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = PartejarTheme.spacing.space16),
        verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
    ) {
        repeat(3) { GestanteCardSkeleton() }
    }
}

@Composable
private fun GestanteCardSkeleton() {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(spacing.space8)
                ) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space20))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.48f).height(spacing.space12))
                }
                PartejarSkeleton(Modifier.size(PartejarTheme.dimensions.minimumTouchTarget))
            }
            PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
            PartejarSkeletonText(Modifier.fillMaxWidth(0.64f).height(spacing.space12))
        }
    }
}

@Composable
private fun StatusFilters(
    selectedStages: Set<GestanteListStage>,
    onStageToggled: (GestanteListStage) -> Unit
) {
    val spacing = PartejarTheme.spacing

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(
                horizontal = spacing.space16,
                vertical = spacing.space12
            ),
        horizontalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        GestanteListStage.entries.forEach { stage ->
            val selected = stage in selectedStages
            val interactionSource = remember { MutableInteractionSource() }
            val pressed by interactionSource.collectIsPressedAsState()
            val containerColor by animateColorAsState(
                targetValue = if (selected) {
                    MaterialTheme.colorScheme.primaryContainer
                } else {
                    MaterialTheme.colorScheme.surfaceContainerLow
                },
                animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
                label = "gestanteFilterContainer"
            )
            val contentColor by animateColorAsState(
                targetValue = if (selected) {
                    MaterialTheme.colorScheme.onPrimaryContainer
                } else {
                    MaterialTheme.colorScheme.onSurfaceVariant
                },
                animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
                label = "gestanteFilterContent"
            )
            val borderColor by animateColorAsState(
                targetValue = if (selected) {
                    MaterialTheme.colorScheme.primary
                } else {
                    MaterialTheme.colorScheme.outline
                },
                animationSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
                label = "gestanteFilterBorder"
            )
            Surface(
                modifier = Modifier
                    .heightIn(min = PartejarTheme.dimensions.minimumTouchTarget)
                    .partejarPressFeedback(pressed, enabled = true)
                    .clip(PartejarShapes.pill)
                    .toggleable(
                        value = selected,
                        role = Role.RadioButton,
                        interactionSource = interactionSource,
                        indication = null,
                        onValueChange = { onStageToggled(stage) }
                    )
                    .semantics { this.selected = selected },
                shape = PartejarShapes.pill,
                color = containerColor,
                contentColor = contentColor,
                border = BorderStroke(
                    PartejarTheme.dimensions.strokeThin,
                    borderColor
                )
            ) {
                Box(
                    Modifier.padding(horizontal = spacing.space12),
                    contentAlignment = Alignment.Center
                ) {
                    Text(stage.label, style = MaterialTheme.typography.labelMedium)
                }
            }
        }
    }
}

@Composable
private fun EmptyGestantes(searchActive: Boolean) {
    Box(
        Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Icon(
                PartejarIcons.Navigation.gestantes,
                contentDescription = null,
                modifier = Modifier.size(PartejarTheme.dimensions.iconLarge),
                tint = MaterialTheme.colorScheme.primary
            )
            Text(
                if (searchActive) "Nenhuma gestante encontrada" else "Nenhuma gestante por aqui",
                modifier = Modifier.padding(top = PartejarTheme.spacing.space12),
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
private fun InitialError(message: String, onRetry: () -> Unit) {
    Column(
        Modifier
            .fillMaxSize()
            .padding(horizontal = PartejarTheme.spacing.space16),
        verticalArrangement = Arrangement.Center
    ) {
        PartejarAlert(title = "Não foi possível carregar", tone = PartejarAlertTone.ERROR) {
            Text(message)
        }
        PartejarButton("Tentar novamente", onRetry, modifier = Modifier.padding(top = PartejarTheme.spacing.space16))
    }
}

private fun GestanteListItemData.toSummary() = GestanteSummary(
    name = name,
    babyName = babyName ?: "Não informado",
    gestationalWeeks = gestationalWeeks,
    gestationalDays = gestationalDays,
    dueDate = dueDate,
    responsible = responsible,
    status = actionRequired?.let(GestanteSummaryStatus::ActionRequired) ?: GestanteSummaryStatus.OnTrack,
    stage = when (stage) {
        GestanteListStage.ACTIVE -> GestanteStage.ACTIVE
        GestanteListStage.POSTPARTUM -> GestanteStage.POSTPARTUM
    },
    deliveryDate = deliveryDate
)

private val GestanteListStage.label: String
    get() = when (this) {
        GestanteListStage.ACTIVE -> "Ativas"
        GestanteListStage.POSTPARTUM -> "Encerradas"
    }
